import { useState, useEffect } from "react";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Users, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const StaffCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (searchTerm = "") => {
    setLoading(true);
    try {
      const response = await api.get(`/staff/customers?search=${searchTerm}&limit=50`);
      setCustomers(response.data.customers);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  const getTierBadge = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return 'badge-gold';
      case 'platinum': return 'badge-platinum';
      default: return 'badge-silver';
    }
  };

  return (
    <StaffLayout title="Customers">
      <div className="space-y-6 animate-fade-in">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="pl-10 bg-[#131316] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
              data-testid="customer-search-input"
            />
          </div>
          <Button type="submit" className="bg-[#D4AF37] text-[#0A0A0B] hover:bg-[#B8942D]" data-testid="search-btn">
            Search
          </Button>
        </form>

        {/* Results count */}
        <p className="text-[#A0A0A0] text-sm">{total} customers found</p>

        {/* Customer List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-20 rounded-xl animate-shimmer" />
            ))}
          </div>
        ) : customers.length > 0 ? (
          <div className="space-y-3">
            {customers.map((customer) => (
              <Link to={`/staff/customers/${customer.id}`} key={customer.id}>
                <Card className="bg-[#131316] border-[#2A2A2F] hover:border-[#D4AF37]/50 transition-colors cursor-pointer" data-testid={`customer-row-${customer.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#1A1A1E] flex items-center justify-center">
                        <span className="text-[#D4AF37] font-semibold text-lg">
                          {customer.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{customer.name}</p>
                        <p className="text-[#6B6B6B] text-sm">{customer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-[#D4AF37] font-medium">
                          {customer.loyalty?.current_points?.toLocaleString() || 0} miles
                        </p>
                        <span className={`badge ${getTierBadge(customer.loyalty?.tier)}`}>
                          {customer.loyalty?.tier || 'Silver'}
                        </span>
                      </div>
                      <ArrowRight size={18} className="text-[#6B6B6B]" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Customers Found</h3>
            <p className="text-[#A0A0A0]">Try adjusting your search</p>
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default StaffCustomers;
